import { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { subscribeGroup } from '../services/realtime';
import { toast } from 'react-toastify';
import { supabase } from '../providers/supabase/client';
import { resetClientStatus } from '../features/clients/clientSlice';
import { resetHotelStatus } from '../features/hotels/hotelSlice';
import { resetAerolineStatus } from '../features/aerolines/aerolineSlice';
import { resetTourStatus } from '../features/tours/tourSlice';
import { resetOperatorStatus } from '../features/operators/operatorSlice';
import { resetCruiseCabinStatus } from '../features/cruiseCabins/cruiseCabinsSlice';

/**
 * useRealtimeSubscriptions — Global hook for Realtime subscriptions.
 *
 * Subscribes to changes on:
 *   - `payments` & `ap_payment_orders` → role-aware toasts
 *   - `clients`, `hotels`, `aerolines`, `tours`, `providers` → cache invalidation + toasts
 *
 * Mount once in DefaultLayout.
 */
export default function useRealtimeSubscriptions({ onPaymentChange, onApOrderChange, sendNotification } = {}) {
  const dispatch = useDispatch();
  const { session } = useSelector((state) => state.auth);
  const userId = session?.user?.id;

  const [profileName, setProfileName] = useState('Asesor');

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('profiles')
      .select('first_name')
      .eq('id', userId)
      .single()
      .then(({ data }) => {
        if (data && data.first_name) {
          setProfileName(data.first_name);
        }
      })
      .catch((err) => console.error('Error fetching profile name:', err));
  }, [userId]);

  // Use refs for callbacks to avoid re-subscribing on every render
  const onPaymentChangeRef = useRef(onPaymentChange);
  const onApOrderChangeRef = useRef(onApOrderChange);
  const sendNotificationRef = useRef(sendNotification);
  useEffect(() => { onPaymentChangeRef.current = onPaymentChange; }, [onPaymentChange]);
  useEffect(() => { onApOrderChangeRef.current = onApOrderChange; }, [onApOrderChange]);
  useEffect(() => { sendNotificationRef.current = sendNotification; }, [sendNotification]);

  const processedNotifications = useRef(new Set());

  const handleNewNotification = useCallback((notification) => {
    if (!notification || !notification.id) return;
    if (processedNotifications.current.has(notification.id)) return;
    processedNotifications.current.add(notification.id);

    console.log('[realtime-notification] Processing new notification:', notification.id, notification.message);

    playNotificationSound();

    toast.info(`🔔 ${notification.title}: ${notification.message}`, {
      autoClose: 8000,
      toastId: `notif-${notification.id}`
    });

    if (notification.alert_speech) {
      console.log('[realtime-notification] Speech is active. Calling playOpenAISpeech...');
      playOpenAISpeech(notification.message, profileName);
    }

    if (document.hidden) {
      sendNotificationRef.current?.(notification.title, {
        body: notification.message,
      });
    }
  }, [profileName]);

  useEffect(() => {
    if (!userId) {
      console.log('[realtime-subscription] No userId, skipping subscription');
      return;
    }

    console.log('[realtime-subscription] Subscribing to erp-realtime for userId:', userId);

    const unsubscribe = subscribeGroup('erp-realtime', [
      // ─── Payments: INSERT (new payment reported) ─────────────────────
      {
        table: 'payments',
        event: 'INSERT',
        callback: (payload) => {
          const payment = payload.new;
          // Don't notify the user who created it
          if (payment.user_id === userId) return;

          if (payment.status === 'PAGO_PENDIENTE_VERIFICACION') {
            toast.info(
              `💰 Nuevo pago reportado — ${payment.amount_pen && Number(payment.amount_pen) > 0 ? `S/${Number(payment.amount_pen).toFixed(2)}` : `$${Number(payment.amount || 0).toFixed(2)}`}`,
              { autoClose: 6000, toastId: `pay-new-${payment.id}` }
            );
            if (document.hidden) {
              sendNotificationRef.current?.('💰 Nuevo pago reportado', {
                body: `Monto: ${payment.amount_pen && Number(payment.amount_pen) > 0 ? `S/${Number(payment.amount_pen).toFixed(2)}` : `$${Number(payment.amount || 0).toFixed(2)}`}`,
              });
            }
          }
          onPaymentChangeRef.current?.(payload);
        }
      },
      // ─── Payments: UPDATE (approved/rejected) ───────────────────────
      {
        table: 'payments',
        event: 'UPDATE',
        callback: (payload) => {
          const payment = payload.new;
          const oldStatus = payload.old?.status;
          const newStatus = payment.status;

          // Only notify on actual status transitions
          if (oldStatus === newStatus) return;

          // Notify the advisor who reported the payment
          if (newStatus === 'PAGO_APROBADO' && payment.user_id === userId) {
            toast.success(
              `✅ Tu pago de ${payment.amount_pen && Number(payment.amount_pen) > 0 ? `S/${Number(payment.amount_pen).toFixed(2)}` : `$${Number(payment.amount || 0).toFixed(2)}`} fue aprobado`,
              { autoClose: 6000, toastId: `pay-ok-${payment.id}` }
            );
            if (document.hidden) {
              sendNotificationRef.current?.('✅ Pago aprobado', {
                body: `Tu pago de ${payment.amount_pen && Number(payment.amount_pen) > 0 ? `S/${Number(payment.amount_pen).toFixed(2)}` : `$${Number(payment.amount || 0).toFixed(2)}`} fue aprobado`,
              });
            }
          }

          if (newStatus === 'PAGO_RECHAZADO' && payment.user_id === userId) {
            toast.error(
              `❌ Tu pago de ${payment.amount_pen && Number(payment.amount_pen) > 0 ? `S/${Number(payment.amount_pen).toFixed(2)}` : `$${Number(payment.amount || 0).toFixed(2)}`} fue rechazado`,
              { autoClose: 8000, toastId: `pay-rej-${payment.id}` }
            );
            if (document.hidden) {
              sendNotificationRef.current?.('❌ Pago rechazado', {
                body: `Tu pago de ${payment.amount_pen && Number(payment.amount_pen) > 0 ? `S/${Number(payment.amount_pen).toFixed(2)}` : `$${Number(payment.amount || 0).toFixed(2)}`} fue rechazado`,
              });
            }
          }

          // Notify admins about any payment status change they didn't trigger
          if (payment.user_id !== userId && newStatus === 'PAGO_PENDIENTE_VERIFICACION') {
            toast.info(
              `💰 Nuevo pago reportado — ${payment.amount_pen && Number(payment.amount_pen) > 0 ? `S/${Number(payment.amount_pen).toFixed(2)}` : `$${Number(payment.amount || 0).toFixed(2)}`}`,
              { autoClose: 6000, toastId: `pay-upd-${payment.id}` }
            );
          }

          onPaymentChangeRef.current?.(payload);
        }
      },
      // ─── AP Orders: INSERT (new B2B payment request) ────────────────
      {
        table: 'ap_payment_orders',
        event: 'INSERT',
        callback: (payload) => {
          const order = payload.new;
          // Don't notify the user who created it
          if (order.requested_by === userId) return;

          if (order.status === 'PENDING_ACCOUNTING') {
            const sym = order.requested_currency === 'PEN' ? 'S/' : '$';
            toast.info(
              `🏦 Nueva solicitud de pago B2B — ${sym}${Number(order.requested_amount || 0).toFixed(2)}`,
              { autoClose: 6000, toastId: `ap-new-${order.id}` }
            );
            if (document.hidden) {
              sendNotificationRef.current?.('🏦 Nueva solicitud B2B', {
                body: `Solicitud de pago: ${sym}${Number(order.requested_amount || 0).toFixed(2)}`,
              });
            }
          }
          onApOrderChangeRef.current?.(payload);
        }
      },
      // ─── AP Orders: UPDATE (processed/confirmed) ────────────────────
      {
        table: 'ap_payment_orders',
        event: 'UPDATE',
        callback: (payload) => {
          const order = payload.new;
          const oldStatus = payload.old?.status;
          const newStatus = order.status;

          // Guard: only react to actual status transitions.
          if (!oldStatus || oldStatus === newStatus) return;

          // Notify the advisor who requested the order
          if (newStatus === 'PAID_CONFIRMED' && order.requested_by === userId) {
            const sym = order.requested_currency === 'PEN' ? 'S/' : '$';
            toast.success(
              `✅ Orden B2B liquidada — ${sym}${Number(order.real_paid_amount || order.requested_amount || 0).toFixed(2)} pagado`,
              { autoClose: 6000, toastId: `ap-ok-${order.id}` }
            );
            if (document.hidden) {
              sendNotificationRef.current?.('✅ Orden B2B liquidada', {
                body: `Pago de ${sym}${Number(order.real_paid_amount || order.requested_amount || 0).toFixed(2)} procesado`,
              });
            }
          }

          onApOrderChangeRef.current?.(payload);
        }
      },
      // ─── Catalogs: clients, hotels, airlines, tours, providers ──────
      {
        table: 'clients',
        event: '*',
        callback: (payload) => {
          setTimeout(() => dispatch(resetClientStatus()), 2000);
          if (payload.eventType === 'INSERT' && payload.new?.created_by !== userId) {
            const name = payload.new?.first_name
              ? `${payload.new.first_name} ${payload.new.last_name || ''}`.trim()
              : payload.new?.name || 'Nuevo cliente';
            toast.info(`👤 Nuevo cliente registrado: ${name}`, { autoClose: 4000, toastId: `client-${payload.new.id}` });
          }
        }
      },
      {
        table: 'hotels',
        event: '*',
        callback: (payload) => {
          setTimeout(() => dispatch(resetHotelStatus()), 2000);
          if (payload.eventType === 'INSERT') {
            toast.info(`🏨 Nuevo hotel agregado: ${payload.new?.name || ''}`, { autoClose: 4000, toastId: `hotel-${payload.new.id}` });
          }
        }
      },
      {
        table: 'aerolines',
        event: '*',
        callback: (payload) => {
          setTimeout(() => dispatch(resetAerolineStatus()), 2000);
          if (payload.eventType === 'INSERT') {
            toast.info(`✈️ Nueva aerolínea agregada: ${payload.new?.name || ''}`, { autoClose: 4000, toastId: `airline-${payload.new.id}` });
          }
        }
      },
      {
        table: 'tours',
        event: '*',
        callback: (payload) => {
          setTimeout(() => dispatch(resetTourStatus()), 2000);
          if (payload.eventType === 'INSERT') {
            toast.info(`🗺️ Nueva excursión agregada: ${payload.new?.name || ''}`, { autoClose: 4000, toastId: `tour-${payload.new.id}` });
          }
        }
      },
      {
        table: 'providers',
        event: '*',
        callback: (payload) => {
          setTimeout(() => dispatch(resetOperatorStatus()), 2000);
          if (payload.eventType === 'INSERT') {
            toast.info(`🤝 Nuevo proveedor agregado: ${payload.new?.name || ''}`, { autoClose: 4000, toastId: `provider-${payload.new.id}` });
          }
        }
      },
      {
        table: 'cruise_cabin_types',
        event: '*',
        callback: (payload) => {
          setTimeout(() => dispatch(resetCruiseCabinStatus()), 2000);
          if (payload.eventType === 'INSERT') {
            toast.info(`🛳️ Nueva cabina de crucero agregada: ${payload.new?.name || ''}`, { autoClose: 4000, toastId: `cabin-${payload.new.id}` });
          }
        }
      },
      // ─── Notifications: INSERT ──────────────────────────────────────
      {
        table: 'notifications',
        event: 'INSERT',
        callback: (payload) => {
          console.log('[realtime-notification] Received INSERT payload via Realtime:', payload);
          const notification = payload.new;
          if (notification.user_id !== userId) return;
          handleNewNotification(notification);
        }
      }
    ]);

    return () => {
      console.log('[realtime-subscription] Cleaning up erp-realtime subscription');
      unsubscribe();
    };
  }, [userId, dispatch, handleNewNotification]);

  // ─── Polling fallback for offline / Realtime failures ───────────────
  useEffect(() => {
    if (!userId) return;

    console.log('[realtime-notification] Starting fallback polling interval...');

    const pollInterval = setInterval(async () => {
      try {
        const oneMinuteAgo = new Date(Date.now() - 60000).toISOString();
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userId)
          .eq('read', false)
          .gte('created_at', oneMinuteAgo);

        if (error) throw error;

        if (data && data.length > 0) {
          console.log('[realtime-notification] Polling fallback found unread notifications:', data.length);
          data.forEach(notification => {
            handleNewNotification(notification);
          });
        }
      } catch (err) {
        console.error('[realtime-notification] Polling fallback error:', err);
      }
    }, 10000); // Poll every 10 seconds

    return () => {
      console.log('[realtime-notification] Stopping fallback polling interval');
      clearInterval(pollInterval);
    };
  }, [userId, handleNewNotification]);
}

function playNotificationSound() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const now = ctx.currentTime;
    
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now); // A5
    osc1.frequency.exponentialRampToValueAtTime(1200, now + 0.15);
    
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(440, now); // A4
    osc2.frequency.exponentialRampToValueAtTime(600, now + 0.15);
    
    gainNode.gain.setValueAtTime(0.2, now);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    
    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    osc1.start(now);
    osc2.start(now);
    
    osc1.stop(now + 0.6);
    osc2.stop(now + 0.6);
  } catch (error) {
    console.error('Failed to play notification sound:', error);
  }
}

async function playOpenAISpeech(text, name = 'Asesor') {
  try {
    const rawText = text.replace(/<[^>]*>/g, '').trim();
    if (!rawText) return;
    const cleanText = `${name} tienes un recordatorio: ${rawText}`;

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || supabase.supabaseUrl;

    const res = await fetch(`${supabaseUrl}/functions/v1/text-to-speech`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ text: cleanText })
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(errText || `HTTP error ${res.status}`);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    await audio.play();
  } catch (err) {
    console.error('Failed to play speech with OpenAI:', err);
    toast.error(`Error en Voz (IA): ${err.message || String(err)}`, { autoClose: 5000 });
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(`${name} tienes un recordatorio: ${text.replace(/<[^>]*>/g, '').trim()}`);
      utterance.lang = 'es-ES';
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  }
}
